export type ModalSize = "sm" | "md" | "lg" | "xl" | "fullscreen";

export interface ModalOpenInput {
    widget?: string;
    widgetId?: string;
    title?: string;
    size?: ModalSize;
    data?: Record<string, unknown>;
}

export interface ModalMessageInput {
    title?: string;
    message?: string;
    confirmText?: string;
    cancelText?: string;
}

export interface ModalProgressInput {
    title?: string;
    message?: string;
    detail?: string;
    value?: number;
    status?: "loading" | "success" | "error";
    steps?: string[];
    activeStep?: number;
    disableClose?: boolean;
    closeText?: string;
}

export interface ModalResult {
    action: "confirm" | "cancel" | "close" | string;
    data?: unknown;
}

export interface ModalProgressHandle {
    closed: Promise<ModalResult>;
    update(input: Partial<ModalProgressInput>): void;
    close(result?: ModalResult): void;
}

export interface ModalRuntimeApi {
    open(input: ModalOpenInput): Promise<ModalResult>;
    alert(input: ModalMessageInput): Promise<ModalResult>;
    confirm(input: ModalMessageInput): Promise<ModalResult>;
    progress(input: ModalProgressInput): ModalProgressHandle;
}

type ModalOpenHandler = (input: ModalOpenInput) => Promise<ModalResult>;
type ModalMessageHandler = (input: ModalMessageInput) => Promise<ModalResult>;
type ModalProgressHandler = (input: ModalProgressInput) => ModalProgressHandle;

let openHandler: ModalOpenHandler | null = null;
let alertHandler: ModalMessageHandler | null = null;
let confirmHandler: ModalMessageHandler | null = null;
let progressHandler: ModalProgressHandler | null = null;

export const registerModalRuntime = (handlers: {
    open: ModalOpenHandler;
    alert: ModalMessageHandler;
    confirm: ModalMessageHandler;
    progress: ModalProgressHandler;
}) => {
    openHandler = handlers.open;
    alertHandler = handlers.alert;
    confirmHandler = handlers.confirm;
    progressHandler = handlers.progress;

    return () => {
        if (openHandler === handlers.open) openHandler = null;
        if (alertHandler === handlers.alert) alertHandler = null;
        if (confirmHandler === handlers.confirm) confirmHandler = null;
        if (progressHandler === handlers.progress) progressHandler = null;
    };
};

export const modalRuntime: ModalRuntimeApi = {
    open(input) {
        if (!openHandler) {
            return Promise.reject(new Error("Modal runtime is not available"));
        }

        return openHandler(input);
    },
    alert(input) {
        if (!alertHandler) {
            return Promise.reject(new Error("Modal runtime is not available"));
        }

        return alertHandler(input);
    },
    confirm(input) {
        if (!confirmHandler) {
            return Promise.reject(new Error("Modal runtime is not available"));
        }

        return confirmHandler(input);
    },
    progress(input) {
        if (!progressHandler) {
            throw new Error("Modal runtime is not available");
        }

        return progressHandler(input);
    },
};
